import {
  Globe,
  Bot,
  Search,
  TrendingUp,
  RefreshCw,
  PhoneCall,
  Sparkles,
  HeartHandshake,
  MapPin,
  Zap,
} from "lucide-react";

export const BUSINESS = {
  name: "Walk-Off Digital",
  short: "Walk-Off",
  owner: "Walker Bazile",
  phone: "504-202-1317",
  phoneHref: "tel:+15042021317",
  email: "wbbazile8@gmail.com",
  emailHref: "mailto:wbbazile8@gmail.com",
  location: "Louisiana",
  founded: "May 26, 2026",
  motto: "Websites built to win.",
  mission:
    "We help local businesses attract more customers through modern websites, AI-powered automation, and digital strategies designed for long-term growth.",
};

export const NAV_LINKS = [
  { label: "Services", to: "/services" },
  { label: "Demos", to: "/demos" },
  { label: "About", to: "/about" },
  { label: "Team", to: "/team" },
];

export const slugify = (s) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const MARQUEE_ITEMS = [
  "WEB DESIGN",
  "AI AUTOMATION",
  "LOCAL SEO",
  "GROWTH STRATEGY",
  "LEAD GENERATION",
  "GOOGLE RANKINGS",
  "BUILT TO WIN",
];

export const CHAPTERS = [
  {
    no: "01",
    title: "The Problem",
    body: "Most local businesses are held back by an outdated website, no website at all, or a page that quietly loses customers every single day. You are great at what you do — the internet just doesn't show it yet.",
  },
  {
    no: "02",
    title: "The Approach",
    body: "We start by understanding your business, your customers, and your goals. Then we build a fast, mobile-first website engineered around one thing: turning visitors into phone calls, appointments, and paying customers.",
  },
  {
    no: "03",
    title: "The Growth",
    body: "A website is only the beginning. With AI-powered automation and a real digital strategy, we help you grow every aspect of your business long after launch — because we win when you win.",
  },
];

export const SERVICES = [
  {
    icon: Globe,
    title: "Custom Website Design",
    tag: "Design & Build",
    desc: "Modern, mobile-first websites that load fast and are effortless to navigate — built to make your business look like the best in town.",
    points: ["Mobile-first & lightning fast", "Clear paths to call & book", "Built to convert visitors"],
  },
  {
    icon: Bot,
    title: "AI-Powered Automation",
    tag: "Automate",
    desc: "Capture leads, answer questions, follow up, and book appointments automatically — so you never lose a customer while you're busy working.",
    points: ["Instant lead follow-up", "Smart booking & reminders", "Automated review requests"],
  },
  {
    icon: Search,
    title: "Local SEO & Google Rankings",
    tag: "Get Found",
    desc: "We optimize your site and Google presence so local customers find you first when they search for what you offer.",
    points: ["On-page SEO foundations", "Google Business optimization", "Rank in your local area"],
  },
  {
    icon: TrendingUp,
    title: "Growth Strategy",
    tag: "Grow",
    desc: "An ongoing partnership focused on growing every aspect of your business — not just handing you a website and disappearing.",
    points: ["Clear growth roadmap", "Monthly guidance", "Adapts to your goals"],
  },
  {
    icon: RefreshCw,
    title: "Website Redesign & Rescue",
    tag: "Modernize",
    desc: "Have an outdated or underperforming site? We modernize it into a premium experience that earns trust instantly.",
    points: ["Fresh modern design", "Faster performance", "Higher engagement"],
  },
  {
    icon: PhoneCall,
    title: "Leads & Conversions",
    tag: "Convert",
    desc: "Every section is engineered to guide visitors toward one thing: contacting your business and becoming a customer.",
    points: ["More phone calls", "More appointments", "Contact forms that work"],
  },
];

export const WHY_US = [
  {
    icon: HeartHandshake,
    title: "Personable & Adaptive",
    desc: "We adapt to your needs. You get a real partner who listens, not a faceless agency running a template.",
  },
  {
    icon: Sparkles,
    title: "More Than a Website",
    desc: "We don't just build you a website — we help your company grow in every aspect of your business.",
  },
  {
    icon: MapPin,
    title: "Built for Louisiana",
    desc: "We understand local businesses and the customers you're trying to reach right here at home.",
  },
  {
    icon: Zap,
    title: "Modern & Fast",
    desc: "Clean, mobile-first, and built on modern technology so your site feels premium on every device.",
  },
];

export const STATS = [
  { value: "100%", label: "Custom-built, never templated" },
  { value: "24h", label: "Typical response time" },
  { value: "Mobile", label: "First on every device" },
  { value: "1:1", label: "Direct access to your builder" },
];

export const REVIEWS = [
  {
    quote:
      "Walker completely transformed how our shop shows up online. We went from an old, clunky page to a website that actually gets us phone calls every week.",
    name: "Marcus Trahan",
    role: "Owner, Trahan's Auto Care",
    location: "Baton Rouge, LA",
  },
  {
    quote:
      "He didn't just build a website — he sat down and helped us think about growing the whole business. Incredibly personable and easy to work with.",
    name: "Danielle Boudreaux",
    role: "Founder, Bayou Bloom Florals",
    location: "Lafayette, LA",
  },
  {
    quote:
      "Our new site loads instantly and looks like something a big-city company would have. Appointments have gone up since we launched.",
    name: "Anthony Rivera",
    role: "Owner, Crescent City Barbershop",
    location: "New Orleans, LA",
  },
  {
    quote:
      "The automation alone saves me hours. New leads get followed up with automatically and I never miss a customer anymore.",
    name: "Chef Nadine Prejean",
    role: "Owner, Nadine's Kitchen",
    location: "Metairie, LA",
  },
  {
    quote:
      "Professional, modern, and fast. Do It Right Digital delivered exactly what our name promises. Highly recommend to any local business.",
    name: "Kevin LeBlanc",
    role: "Owner, LeBlanc Lawn & Landscape",
    location: "Slidell, LA",
  },
  {
    quote:
      "I finally have a website I'm proud to send people to. It's clean, easy to use, and it makes us look trustworthy right away.",
    name: "Simone Guidry",
    role: "Owner, Guidry Cleaning Co.",
    location: "Houma, LA",
  },
];

export const FAQS = [
  {
    q: "How long does it take to build my website?",
    a: "Most local business websites launch within 2–4 weeks, depending on the number of pages and content. We'll give you a clear timeline before we begin, and we keep you updated the entire way.",
  },
  {
    q: "What if I don't have a website yet?",
    a: "Perfect — that's one of our favorite starting points. We handle everything from strategy and design to copywriting and launch, so you get a professional presence without the stress.",
  },
  {
    q: "Do you only build websites?",
    a: "No. A website is just the start. We also offer AI-powered automation, local SEO, and ongoing growth strategy to help you grow every aspect of your business.",
  },
  {
    q: "Will my website work on phones?",
    a: "Always. Every site we build is mobile-first, meaning it's designed for phones first and looks flawless on tablets and desktops too.",
  },
  {
    q: "Do you offer payment plans?",
    a: "Yes. We offer flexible financing options so you can get a premium website now and pay in comfortable monthly installments. Visit our Financing page or just ask.",
  },
  {
    q: "How do I get started?",
    a: "Give us a call at 504-202-1317 or request a free quote through our contact form. We'll learn about your business and map out exactly how to help you grow.",
  },
];

export const TEAM = [
  {
    name: "Walker Bazile",
    role: "Founder & Owner",
    bio: "Walker founded Walk-Off Digital to give local businesses websites as sharp as the big brands. He personally handles every project from strategy to launch.",
    img: "/team-walker.jpg?v=2",
  },
];

export const GALLERY = [
  { img: "https://images.unsplash.com/photo-1597672996375-4d21cad0cbb9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", title: "Bayou Bloom Florals", type: "Website + Booking", span: "md:col-span-7" },
  { img: "https://images.unsplash.com/photo-1678690832311-bb6e361989ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", title: "Trahan's Auto Care", type: "Website + Local SEO", span: "md:col-span-5" },
  { img: "https://images.unsplash.com/photo-1604074131228-9d48b811bd80?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", title: "Crescent City Barbershop", type: "Website + Automation", span: "md:col-span-5" },
  { img: "https://images.pexels.com/photos/6638402/pexels-photo-6638402.png?auto=compress&cs=tinysrgb&w=1200", title: "Nadine's Kitchen", type: "Website + Ordering", span: "md:col-span-7" },
  { img: "https://images.unsplash.com/photo-1637502875124-eb4a9843a2fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", title: "LeBlanc Landscape", type: "Website Redesign", span: "md:col-span-7" },
  { img: "https://images.pexels.com/photos/18105/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=1200", title: "Guidry Cleaning Co.", type: "Website + Leads", span: "md:col-span-5" },
];

export const PROCESS = [
  { no: "01", title: "Discover", desc: "We learn your business, your customers, and your goals." },
  { no: "02", title: "Design", desc: "We craft a clean, premium, conversion-focused design." },
  { no: "03", title: "Build", desc: "We develop a fast, mobile-first site with automation baked in." },
  { no: "04", title: "Grow", desc: "We launch, optimize, and keep helping you grow." },
];

export const FINANCING_PLANS = [
  {
    name: "Starter",
    price: "$99",
    period: "/mo",
    highlight: false,
    desc: "For businesses that need a professional presence, fast.",
    squareUrl: "",
    features: ["1–3 page website", "Mobile-first design", "Click-to-call & contact form", "Basic local SEO setup", "Launch in ~2 weeks"],
  },
  {
    name: "Growth",
    price: "$199",
    period: "/mo",
    highlight: true,
    desc: "Our most popular plan for businesses ready to scale.",
    squareUrl: "",
    features: ["Up to 7 custom pages", "AI lead-capture automation", "Local SEO & Google Business", "Automated review requests", "Monthly growth guidance"],
  },
  {
    name: "Partner",
    price: "Custom",
    period: "",
    highlight: false,
    desc: "A full growth partnership tailored to your business.",
    squareUrl: "",
    features: ["Unlimited pages & features", "Advanced AI automation", "Ongoing SEO & content", "Priority support", "Dedicated strategy sessions"],
  },
];

export const DEMOS = [
  {
    name: "Palmisano Family Dentistry",
    category: "Dental Practice Website",
    url: "https://teeth-points.preview.emergentagent.com/?utm_source=share",
    desc: "A clean, modern website for a family dental practice — built to build trust, showcase services, and turn visitors into booked appointments.",
    tags: ["Web Design", "Local Business", "Appointments"],
  },
  {
    name: "Buildin Woodlake",
    category: "Business Website",
    url: "https://www.buildinwoodlake.com",
    desc: "A clean, modern website built to showcase the business, build trust, and turn visitors into customers.",
    tags: ["Web Design", "Local Business"],
  },
  {
    name: "Ms. Jeanette's Snowballs",
    category: "Local Snowball Stand",
    url: "https://www.msjeanettesnowballs.com/",
    desc: "A fun, mobile-friendly website for a local snowball stand — built to share the menu, hours, and location and bring in more customers.",
    tags: ["Web Design", "Local Business", "Menu"],
  },
];

export const NAV_MENU = [
  { label: "Home", to: "/" },
  {
    label: "Services",
    to: "/services",
    children: SERVICES.map((s) => ({
      label: s.title,
      to: `/services#${slugify(s.title)}`,
      desc: s.tag,
      icon: s.icon,
    })),
  },
  { label: "Demos", to: "/demos" },
  {
    label: "Company",
    children: [
      { label: "About Us", to: "/about", desc: "Our story & mission" },
      { label: "Meet the Team", to: "/team", desc: "The people behind it" },
    ],
  },
  { label: "Contact", to: "/contact" },
];
